import dayjs from 'dayjs';

import Queue from '@/shared/types/fulfilment/queue.type';
import Order from '@/shared/types/order/order.type';

import ProductTableRow from '../components/CustomsProductTableForm/types/productTableRow.type';
import usePackagingDetailsTableForm_v2 from '../components/PackagingDetailsTableForm_v2/hooks/usePackagingDetailsTableForm_v2';
import usePickupDetailsForm from '../components/PickupDetailsForm/hooks/usePickupDetailsForm';
import useSetOriginForm from '../components/SetOriginForm/hooks/useSetOriginForm';
import useTaxRecipientForm from '../components/TaxRecipientForm/hooks/useTaxRecipientForm';

type Args = {
  order: Order;
  customProductsTableData: ProductTableRow[];
  selectedQueues: Queue[];
  packagesDetailsTableState: ReturnType<typeof usePackagingDetailsTableForm_v2>;
  pickupDetailsState: ReturnType<typeof usePickupDetailsForm>;
  taxRecipientState: ReturnType<typeof useTaxRecipientForm>;
  originFormState: ReturnType<typeof useSetOriginForm>;
  isCustoms?: boolean;
};

const getSubmitRequestBody = ({
  order,
  customProductsTableData,
  selectedQueues,
  packagesDetailsTableState,
  pickupDetailsState,
  taxRecipientState,
  originFormState,
  isCustoms,
}: Args) => {
  const { in_order_id } = order;
  const { packages } = packagesDetailsTableState;
  const { pickupDetailsForm } = pickupDetailsState;
  const { taxRecipientForm, taxRecipientFormOptional } = taxRecipientState;
  const { pickOriginForm } = originFormState;

  const requestBody = {
    in_order_id,
    fulfilment_queue_items: isCustoms
      ? customProductsTableData.map((item) => ({
          fulfilment_queue_id: item.fulfilment_queue_id,
          product_name: item.product_name,
          unit_price: item.unit_price,
          product_description: item.product_description.substring(0, 30),
          country_of_origin: item.country_of_origin,
          weight: item.weight,
        }))
      : selectedQueues.map((item) => ({
          fulfilment_queue_id: item.fulfilment_queue_id,
        })),

    shipping_details: {
      fulfilment_origin_id: pickOriginForm.fulfilmentOrigin,
      pickup_details: {
        pre_scheduled_pickup: pickupDetailsForm.pre_scheduled_pickup,
        ts_ready_at: dayjs(pickupDetailsForm.ts_ready_at).format(
          'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
        ),
        ts_ready_until: dayjs(pickupDetailsForm.ts_ready_until).format(
          'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
        ),
        pickup_location: pickupDetailsForm.pickup_location,
        contact_name: pickupDetailsForm.contact_name,
        contact_phone_number: {
          number: pickupDetailsForm.contact_phone_number.number,
          extension: pickupDetailsForm.contact_phone_number.extension,
        },
      },
      customs_invoice: isCustoms
        ? {
            source: pickOriginForm.source,
            broker: pickOriginForm.use_carrier
              ? {
                  use_carrier: pickOriginForm.use_carrier,
                }
              : {
                  use_carrier: pickOriginForm.use_carrier,
                  name: pickOriginForm.name,
                  account_number: pickOriginForm.account_number,
                  phone_number: pickOriginForm.phone_number,
                  email_address: pickOriginForm.email_address,
                  fax_number: pickOriginForm.fax_number,
                  usmc_number: pickOriginForm.usmc_number,
                  fda_number: pickOriginForm.fda_number,
                },
            details: {
              tax_recipient: {
                type: taxRecipientForm.type,
                reason_for_export: taxRecipientForm.reason_for_export,
                shipper_tax_identifier:
                  taxRecipientFormOptional.shipper_tax_identifier,
                receiver_tax_identifier:
                  taxRecipientFormOptional.receiver_tax_identifier,
                third_party_tax_identifier:
                  taxRecipientFormOptional.third_party_tax_identifier,
                additional_remarks: taxRecipientFormOptional.additional_remarks,
                comments: taxRecipientFormOptional.comments,
              },
            },
          }
        : undefined,
      packaging_definition: packages.map((packageItem) => ({
        cuboid: packageItem.package_cuboid,
        weight: packageItem.package_weight,
        description: packageItem.package_description,
      })),
    },
  };

  return requestBody;
};

export default getSubmitRequestBody;
